import React, { useState, useEffect } from 'react';

const CacheManager = ({
  roomDimensions,
  furniture,
  onRestoreLayout,
  onClearCache
}) => {
  const [savedLayouts, setSavedLayouts] = useState([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [layoutName, setLayoutName] = useState('');
  const [currentRestoredLayout, setCurrentRestoredLayout] = useState(null);

  // 加载所有保存的布局
  useEffect(() => {
    loadSavedLayouts();
  }, []);

  const loadSavedLayouts = () => {
    try {
      const layouts = JSON.parse(localStorage.getItem('roomLayouts') || '[]');
      setSavedLayouts(layouts);
    } catch (error) {
      console.error('加载缓存失败:', error);
      setSavedLayouts([]);
    }
  };

  const saveCurrentLayout = () => {
    if (!layoutName.trim()) {
      return;
    }

    const currentLayout = {
      id: currentRestoredLayout ? currentRestoredLayout.id : Date.now(),
      name: layoutName.trim(),
      timestamp: new Date().toISOString(),
      roomDimensions,
      furniture
    };

    try {
      const existingLayouts = JSON.parse(localStorage.getItem('roomLayouts') || '[]');
      let updatedLayouts;
      
      if (currentRestoredLayout) {
        // 如果是从恢复的布局，则更新该布局
        updatedLayouts = existingLayouts.map(layout => 
          layout.id === currentRestoredLayout.id ? currentLayout : layout
        );
      } else {
        // 否则添加新布局
        updatedLayouts = [...existingLayouts, currentLayout];
      }
      
      localStorage.setItem('roomLayouts', JSON.stringify(updatedLayouts));
      
      setSavedLayouts(updatedLayouts);
      setLayoutName('');
      setCurrentRestoredLayout(null);
      setShowSaveDialog(false);
    } catch (error) {
      console.error('保存失败:', error);
    }
  };

  const restoreLayout = (layout) => {
    onRestoreLayout(layout.roomDimensions, layout.furniture);
    setCurrentRestoredLayout(layout); // 记录当前恢复的布局
    setLayoutName(layout.name); // 设置布局名称
    setShowSaveDialog(false); // 恢复后关闭弹框
  };

  const deleteLayout = (layoutId) => {
    try {
      const updatedLayouts = savedLayouts.filter(layout => layout.id !== layoutId);
      localStorage.setItem('roomLayouts', JSON.stringify(updatedLayouts));
      setSavedLayouts(updatedLayouts);
    } catch (error) {
      console.error('删除失败:', error);
    }
  };

  const clearAllCache = () => {
    try {
      localStorage.removeItem('roomLayouts');
      setSavedLayouts([]);
      onClearCache();
    } catch (error) {
      console.error('清除缓存失败:', error);
    }
  };

  return (
    <div className="layout-management">
      <button 
        className="layout-manager-button"
        onClick={() => setShowSaveDialog(true)}
      >
        布局管理 ({savedLayouts.length})
      </button>

      {/* 布局管理对话框 */}
      {showSaveDialog && (
        <div className="save-dialog">
          <div className="dialog-content layout-dialog">
            <div className="dialog-header">
              <h4>布局管理</h4>
              <button 
                className="close-button"
                onClick={() => setShowSaveDialog(false)}
              >
                ×
              </button>
            </div>
            
            {/* 保存当前布局 */}
            <div className="save-section">
              <h5>
                {currentRestoredLayout ? `更新布局: ${currentRestoredLayout.name}` : '保存当前布局'}
              </h5>
              <div className="save-input-group">
                <input
                  type="text"
                  placeholder="输入布局名称"
                  value={layoutName}
                  onChange={(e) => setLayoutName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && saveCurrentLayout()}
                />
                <button 
                  className="save-current-button"
                  onClick={saveCurrentLayout}
                  disabled={!layoutName.trim()}
                >
                  {currentRestoredLayout ? '更新' : '保存'}
                </button>
              </div>
              {currentRestoredLayout && (
                <p className="restore-notice">
                  当前已恢复布局，保存将更新该布局
                </p>
              )}
            </div>

            {/* 已保存的布局列表 */}
            <div className="saved-layouts-section">
              <h5>已保存的布局 ({savedLayouts.length})</h5>
              {savedLayouts.length === 0 ? (
                <p className="empty-message">暂无保存的布局</p>
              ) : (
                <div className="layout-list">
                  {savedLayouts.map((layout) => (
                    <div key={layout.id} className="layout-item">
                      <div className="layout-info">
                        <span className="layout-name">{layout.name}</span>
                        <span className="layout-date">
                          {new Date(layout.timestamp).toLocaleString()}
                        </span>
                        <span className="layout-stats">
                          {layout.furniture.length} 件家具
                        </span>
                      </div>
                      <div className="layout-actions">
                        <button 
                          className="restore-button"
                          onClick={() => restoreLayout(layout)}
                        >
                          恢复
                        </button>
                        <button 
                          className="delete-layout-button"
                          onClick={() => deleteLayout(layout.id)}
                        >
                          删除
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 清除所有缓存 */}
            {savedLayouts.length > 0 && (
              <div className="clear-cache-section">
                <button 
                  className="clear-all-button"
                  onClick={clearAllCache}
                >
                  清除所有缓存
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CacheManager; 